// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { GroupSecurity } from '@quromedical/fhir-common';
import { CreateMediaRequest } from '@quromedical/models';
import { getDataFromResponse, mediaColumns, MediaForm } from 'core/media';
import { PatientHeaderSection } from 'core/patient';
import { PaginatedTable } from 'core/table';
import { Section, Skeleton, SkeletonProvider } from 'design-system';
import { logger } from 'helpers';
import { useUserSession } from 'hooks';
import { useFiniteState } from 'hooks/useFiniteState';
import { FetcherOptions } from 'hooks/usePaginatedData';
import { useResponsive } from 'hooks/useResponsive';
import { useRevalidation } from 'hooks/useRevalidation';
import { usePatientTasks } from 'hooks/useTasks';
import { MediaApi } from 'integration';
import { MediaApi as LegacyMediaApi, MediaEndpoint } from 'integration/aggregate';
import { PatientDrawerScreenProps } from 'navigation';
import { usePatientId } from 'providers/PatientIdContext';
import React, { useCallback, useMemo } from 'react';
import { ScrollView } from 'react-native';
import useSWR from 'swr';

const api = new MediaApi();
const legacyAPi = new LegacyMediaApi();

type FormStep = 'form' | 'editing' | 'submitting' | 'error';

export const MediaListScreen: React.FC<PatientDrawerScreenProps<'Medias'>> = () => {
  const showHeader = useResponsive({ md: true }, false);

  const patientId = usePatientId();
  const usePatientTaskProps = usePatientTasks({ subject: patientId });
  const { isLoading: isTasksLoading } = usePatientTaskProps;

  const fetcher = useCallback(
    (options: FetcherOptions) =>
      api.getAllMedia({
        subject: patientId,
        pageToken: options?.nextToken,
        ...options,
      }),
    [patientId]
  );

  const swrKey = legacyAPi.getMediaSWRKey(patientId, MediaEndpoint.Base);
  const revalidate = useRevalidation(patientId);
  const session = useUserSession();
  const { isValidating: isNotesValidating } = useSWR(swrKey, fetcher);
  const isLoading = isTasksLoading || isNotesValidating;

  const hasMediaPermissions = session.hasAny([GroupSecurity.MediaUpdate]);
  const hasTaskPermissions = session.hasAny([GroupSecurity.TaskCreate, GroupSecurity.TaskUpdate]);

  const columns = useMemo(
    () => mediaColumns({ patientId, hasMediaPermissions, hasTaskPermissions }),
    [hasMediaPermissions, hasTaskPermissions, patientId]
  );

  const state = useFiniteState<FormStep>('form');

  const onSubmit = useCallback(
    async (formData: CreateMediaRequest) => {
      state.set('submitting');
      try {
        await api.createMedia(patientId, formData);
        await revalidate();
        state.set('editing');
      } catch (err) {
        logger.error(err);
        state.set('error');
      }
    },
    [state, patientId, revalidate]
  );

  return (
    <ScrollView>
      <PatientHeaderSection id={patientId} />

      <Section>
        <SkeletonProvider loading={state.is('submitting')}>
          <Skeleton>
            <MediaForm patientId={patientId} onSubmit={onSubmit} />
          </Skeleton>
        </SkeletonProvider>
      </Section>

      <Section hasBottomMargin>
        <SkeletonProvider loading={isLoading}>
          <PaginatedTable
            cacheKey={`Media${patientId}`}
            fetcher={fetcher}
            columns={columns}
            getDataFromResponse={getDataFromResponse}
            showHeader={showHeader}
          />
        </SkeletonProvider>
      </Section>
    </ScrollView>
  );
};
